import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/ece6b6f1-17c7-4315-affc-13f36c335c8a.png" alt="MIRO d.o.o. Logo" className="h-10 w-10 object-contain" />
              <span className="text-xl font-medium text-gray-900">MIRO d.o.o.</span>
            </div>
            <div className="flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-sm font-light text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-light text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('products')} className="text-sm font-light text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Products</button>
              <button onClick={() => scrollToSection('features')} className="text-sm font-light text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Features</button>
              <button onClick={() => scrollToSection('security')} className="text-sm font-light text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Security</button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-light text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-white via-gray-50 to-gray-100 pt-20">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-7xl font-extralight tracking-tight text-gray-900 leading-tight mb-10">
            Modern iOS Applications<br />
            Built for Performance<br />
            & Security
          </h1>
          <p className="text-lg font-light text-gray-600 max-w-2xl mx-auto mb-20">
            Expert team developing cutting-edge solutions for iOS platform
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div className="bg-gray-800 text-white px-6 py-3 rounded-full flex items-center gap-2 whitespace-nowrap">
              <i className="ri-shield-check-line text-lg"></i>
              <span className="text-sm">VPN Solutions</span>
            </div>
            <div className="bg-gray-800 text-white px-6 py-3 rounded-full flex items-center gap-2 whitespace-nowrap">
              <i className="ri-speed-line text-lg"></i>
              <span className="text-sm">Device Optimization</span>
            </div>
            <div className="bg-gray-800 text-white px-6 py-3 rounded-full flex items-center gap-2 whitespace-nowrap">
              <i className="ri-lock-line text-lg"></i>
              <span className="text-sm">Enhanced Security</span>
            </div>
            <div className="bg-gray-800 text-white px-6 py-3 rounded-full flex items-center gap-2 whitespace-nowrap">
              <i className="ri-user-lock-line text-lg"></i>
              <span className="text-sm">User Privacy</span>
            </div>
            <div className="bg-gray-800 text-white px-6 py-3 rounded-full flex items-center gap-2 whitespace-nowrap">
              <i className="ri-dashboard-line text-lg"></i>
              <span className="text-sm">Performance Tools</span>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-slate-900 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            <div className="lg:col-span-2">
              <h2 className="text-6xl font-serif text-white mb-16">Who We Are</h2>
              <p className="text-gray-300 text-base leading-relaxed mb-8">
                MIRO d.o.o. is a dedicated team of experts specializing in developing modern applications for the iOS platform. We combine technical excellence with innovative design to create products that truly make a difference in users' daily lives.
              </p>
              <p className="text-gray-300 text-base leading-relaxed">
                Our focus on security, performance, and user experience drives everything we do. From VPN solutions to device optimization tools, we build applications that users can trust and rely on every day.
              </p>
            </div>
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-slate-800 rounded-3xl p-8 hover:bg-slate-700 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-apple-line text-white text-4xl"></i>
                  </div>
                  <h3 className="text-white text-xl font-light">iOS Platform Expertise</h3>
                </div>
              </div>
              <div className="bg-slate-800 rounded-3xl p-8 hover:bg-slate-700 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-code-s-slash-line text-white text-4xl"></i>
                  </div>
                  <h3 className="text-white text-xl font-light">Modern Development Practices</h3>
                </div>
              </div>
              <div className="bg-slate-800 rounded-3xl p-8 hover:bg-slate-700 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-user-heart-line text-white text-4xl"></i>
                  </div>
                  <h3 className="text-white text-xl font-light">User-Centric Design</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">OUR PRODUCTS</p>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">VPN & Cleaner</h2>
            <p className="text-base text-gray-600">Designed to enhance experience and security</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* VPN Product Card */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-3xl p-16 m-6 flex items-center justify-center" style={{ height: '320px' }}>
                <div className="w-full h-full flex items-center justify-center">
                  <img src="https://public.readdy.ai/ai/img_res/a931a86b9c8cc7346d62a18a5e0da2f2.jpg" alt="VPN Solution" className="w-full h-full object-contain" />
                </div>
              </div>
              <div className="p-8 text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">VPN Solution</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Secure your online presence with our advanced VPN technology. Browse privately and access content from anywhere in the world.
                </p>
                <ul className="text-left text-xs text-gray-500 space-y-2">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-0.5"></i>
                    <span>Military-grade encryption for all your data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-0.5"></i>
                    <span>Global server network with high-speed connections</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-0.5"></i>
                    <span>No-logs policy ensuring complete privacy</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-3xl p-16 m-6 flex items-center justify-center" style={{ height: '320px' }}>
                <div className="w-full h-full flex items-center justify-center">
                  <img src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20device%20cleaning%20icon%20with%20optimization%20symbols%20on%20soft%20gradient%20purple%20background%2C%20clean%20professional%20iOS%20app%20interface%20design%2C%20performance%20boost%20illustration%20with%20speed%20indicators%2C%20contemporary%20tech%20design%20with%20geometric%20elements%20and%20subtle%20depth&width=400&height=320&seq=cleaner001&orientation=landscape" alt="Cleaner Tool" className="w-full h-full object-contain" />
                </div>
              </div>
              <div className="p-8 text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Cleaner Tool</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Optimize your device performance with intelligent cleaning. Remove junk files and boost your iOS device speed effortlessly.
                </p>
                <ul className="text-left text-xs text-gray-500 space-y-2">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-0.5"></i>
                    <span>Smart storage analysis and optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-0.5"></i>
                    <span>Safe removal of duplicate and cached files</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-0.5"></i>
                    <span>Battery and memory performance enhancement</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative mb-16">
            <div className="text-center">
              <h2 className="text-7xl font-extralight text-gray-900 mb-5">
                Key <span className="text-teal-600">Features</span>
              </h2>
              <p className="text-base text-gray-600">Everything you need for optimal iOS experience</p>
            </div>
            <div className="absolute top-0 right-0 bg-gray-900 text-white px-6 py-2 rounded-full text-xs whitespace-nowrap">
              2025 Edition
            </div>
          </div>
          <div className="overflow-x-auto pb-6 -mx-6 px-6">
            <div className="flex gap-5" style={{ width: 'max-content' }}>
              {/* Feature Card 1 */}
              <div className="bg-gray-50 rounded-2xl p-6" style={{ width: '280px', height: '360px' }}>
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center mb-5">
                  <i className="ri-shield-check-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Advanced Protection</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-auto">
                  Multi-layered security protocols protect your data from threats and unauthorized access at all times.
                </p>
                <div className="mt-auto pt-6">
                  <div className="text-3xl font-bold text-gray-900">99.9%</div>
                  <div className="text-xs text-gray-500 mt-1">Threat Detection</div>
                </div>
              </div>

              {/* Feature Card 2 */}
              <div className="bg-gray-50 rounded-2xl p-6" style={{ width: '280px', height: '360px' }}>
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center mb-5">
                  <i className="ri-speed-up-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Lightning Fast</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-auto">
                  Optimized algorithms ensure maximum speed without compromising security or device performance.
                </p>
                <div className="mt-auto pt-6">
                  <div className="text-3xl font-bold text-gray-900">3x</div>
                  <div className="text-xs text-gray-500 mt-1">Faster Performance</div>
                </div>
              </div>

              {/* Feature Card 3 */}
              <div className="bg-gray-50 rounded-2xl p-6" style={{ width: '280px', height: '360px' }}>
                <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center mb-5">
                  <i className="ri-user-lock-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Privacy First</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-auto">
                  Your data stays yours. We never collect, store, or share your personal information with anyone.
                </p>
                <div className="mt-auto pt-6">
                  <div className="text-3xl font-bold text-gray-900">Zero</div>
                  <div className="text-xs text-gray-500 mt-1">Data Logging</div>
                </div>
              </div>

              {/* Feature Card 4 */}
              <div className="bg-gray-50 rounded-2xl p-6" style={{ width: '280px', height: '360px' }}>
                <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center mb-5">
                  <i className="ri-global-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Global Network</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-auto">
                  Access servers worldwide for unrestricted browsing and optimal connection speeds wherever you are.
                </p>
                <div className="mt-auto pt-6">
                  <div className="text-3xl font-bold text-gray-900">50+</div>
                  <div className="text-xs text-gray-500 mt-1">Server Locations</div>
                </div>
              </div>

              {/* Feature Card 5 */}
              <div className="bg-gray-50 rounded-2xl p-6" style={{ width: '280px', height: '360px' }}>
                <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center mb-5">
                  <i className="ri-smartphone-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">iOS Optimized</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-auto">
                  Built specifically for iOS devices, ensuring seamless integration and native performance.
                </p>
                <div className="mt-auto pt-6">
                  <div className="text-3xl font-bold text-gray-900">100%</div>
                  <div className="text-xs text-gray-500 mt-1">iOS Compatible</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="rounded-3xl overflow-hidden" style={{ height: '500px' }}>
              <img src="https://readdy.ai/api/search-image?query=Abstract%20modern%20security%20shield%20with%20lock%20and%20encryption%20symbols%20on%20gradient%20teal%20and%20blue%20background%2C%20minimalist%20tech%20illustration%20with%20geometric%20patterns%20and%20network%20connections%2C%20professional%20cybersecurity%20design%20with%20depth%20and%20subtle%20lighting%20effects%2C%20contemporary%20digital%20protection%20visual&width=600&height=500&seq=security001&orientation=landscape" alt="Security" className="w-full h-full object-cover" />
            </div>
            <div className="bg-gray-900 rounded-3xl p-16">
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-6">SECURITY FIRST</p>
              <h2 className="text-5xl font-light text-white mb-8 leading-tight">
                Enterprise-Grade<br />Protection
              </h2>
              <p className="text-base text-gray-300 leading-relaxed mb-10 max-w-lg">
                We implement the highest security standards to protect your data and privacy. Our applications use cutting-edge encryption and security protocols trusted by millions of users worldwide.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-white text-xl"></i>
                  <span className="text-white text-base">End-to-End Encryption</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-white text-xl"></i>
                  <span className="text-white text-base">Zero-Log Policy</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-white text-xl"></i>
                  <span className="text-white text-base">Advanced Threat Detection</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            <div className="lg:col-span-2">
              <h2 className="text-5xl font-bold text-gray-900 mb-10 leading-tight">Get in<br />Touch</h2>
              <p className="text-base text-gray-600 leading-relaxed max-w-md mb-16">
                Have questions about our products or need support? We're here to help. Reach out to us through any of the contact methods listed, and our team will get back to you promptly.
              </p>
            </div>
            <div className="lg:col-span-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                {/* Address Column */}
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-4">ADDRESS</p>
                  <div className="space-y-3">
                    <p className="text-base text-gray-700 flex items-start gap-2">
                      <i className="ri-map-pin-line text-gray-500 mt-1"></i>
                      <span>Put Kapelice 6</span>
                    </p>
                    <p className="text-base text-gray-700 flex items-start gap-2">
                      <i className="ri-building-line text-gray-500 mt-1"></i>
                      <span>Trogir</span>
                    </p>
                    <p className="text-base text-gray-700 flex items-start gap-2">
                      <i className="ri-mail-line text-gray-500 mt-1"></i>
                      <span>21220</span>
                    </p>
                    <p className="text-base text-gray-700 flex items-start gap-2">
                      <i className="ri-global-line text-gray-500 mt-1"></i>
                      <span>Croatia</span>
                    </p>
                  </div>
                </div>

                {/* Contact Column */}
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-4">CONTACT</p>
                  <div className="space-y-3">
                    <a href="mailto:help@mirodoo.com" className="text-base text-gray-700 flex items-start gap-2 hover:text-teal-600 transition-colors cursor-pointer">
                      <i className="ri-mail-line text-gray-500 mt-1"></i>
                      <span>help@mirodoo.com</span>
                    </a>
                    <a href="tel:+12512775845" className="text-base text-gray-700 flex items-start gap-2 hover:text-teal-600 transition-colors cursor-pointer">
                      <i className="ri-phone-line text-gray-500 mt-1"></i>
                      <span>+1 2512775845</span>
                    </a>
                  </div>
                </div>

                {/* Company Column */}
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-4">COMPANY</p>
                  <div className="space-y-3">
                    <p className="text-base text-gray-700">MIRO d.o.o.</p>
                    <p className="text-sm text-gray-500">Since 2025</p>
                    <p className="text-sm text-gray-500">iOS Excellence</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-xs text-gray-500">
            <p>© 2025 MIRO d.o.o. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
